import {unref} from 'vue';
import {
  getRecordSubTableRecords,
  getSubTableFieldKey,
  isNestedDisplaySubTableColumn,
} from '@/main-page/utils/xmSubTableColumnUtils';
import {resolveColumnRequired} from '@/main-page/gridCellEditor/validate/columnRequired';

const SKIP_CONTROL_TYPES = new Set(['operation', 'orderNum', 'columnsConfig']);

function getCellValue(row: any, col: any) {
  const dataIndex = col.dataIndex || col.propId;
  if (!dataIndex) {
    return undefined;
  }
  const val = row[dataIndex];
  if (val != null && val !== '') {
    return val;
  }
  if (col.propId && col.propId !== dataIndex) {
    return row[col.propId];
  }
  return val;
}

export type ValidateCellError = {
  itemId: string;
  flag: true;
  msg: string;
  rowIndex: number;
  fieldName: string;
  fieldLabel: string;
  nestedColLabel?: string;
  nestedRowIndex?: number;
  parentRowIndex?: number;
};

function matchRegexError(reg: RegExp, cellValue: any) {
  return reg.test(String(cellValue ?? ''));
}

/** 非空值不符合 pattern 时视为格式错误（与 xmForm validateCom 中 pattern 规则一致） */
function isFormatInvalid(reg: RegExp, cellValue: any) {
  if (cellValue == null || cellValue === '') {
    return false;
  }
  return !matchRegexError(reg, cellValue);
}

/**
 * 校验单个单元格（与 useEditorValidate.validateAll 规则一致，不依赖 DOM 挂载）
 */
export function validateRecordCell({
  record,
  column,
  rowIndex,
  state,
}: {
  record: any;
  column: any;
  rowIndex: number;
  /** 页面全局状态或取状态的函数；不传时由动态必填自行获取 */
  state?: any;
}): ValidateCellError | null {
  const col = unref(column);
  const row = unref(record);
  const dataIndex = col?.dataIndex || col?.propId;
  if (!dataIndex || !row) {
    return null;
  }
  if (SKIP_CONTROL_TYPES.has(col.controlType) || col.propType === 'operation') {
    return null;
  }

  const cellValue = getCellValue(row, col);
  const fieldLabel = col.propName || col.title || dataIndex;

  const buildError = (msg: string): ValidateCellError => ({
    itemId: dataIndex,
    flag: true,
    msg,
    rowIndex,
    fieldName: dataIndex,
    fieldLabel,
  });

  if (resolveColumnRequired(col, row, state) && (cellValue == null || cellValue === '')) {
    return buildError('请填写' + fieldLabel);
  }

  if ((col.isTelValidate + '') === 'true') {
    if (isFormatInvalid(/^1(3\d|4[5-9]|5[0-35-9]|6[2567]|7[0-8]|8\d|9[0-35-9])\d{8}$/, cellValue)) {
      return buildError('请输入正确手机号-' + fieldLabel);
    }
  }
  if ((col.isNumberValidate + '') === 'true') {
    if (isFormatInvalid(/^-?\d+\.\d+$|^-?\d+$/, cellValue)) {
      return buildError('数字格式不正确-' + fieldLabel);
    }
  }
  if ((col.isFirstUpper + '') === 'true') {
    if (isFormatInvalid(/^[A-Z].*/, cellValue)) {
      return buildError('首写字母要大写-' + fieldLabel);
    }
  }
  if ((col.isFirstLower + '') === 'true') {
    if (isFormatInvalid(/^[a-z].*/, cellValue)) {
      return buildError('首写字母要小写-' + fieldLabel);
    }
  }
  if ((col.isBigZreo + '') === 'true' && cellValue != null && cellValue !== '') {
    if (parseFloat(cellValue) < 0) {
      return buildError('数值需要大于0-' + fieldLabel);
    }
  }
  if ((col.isEmailValidate + '') === 'true') {
    if (isFormatInvalid(/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/, cellValue)) {
      return buildError('邮箱格式不正确-' + fieldLabel);
    }
  }

  return null;
}

/**
 * 遍历表格全量数据校验（含嵌套子表递归校验）
 */
export function validateAllTableRecords({
  tableDs,
  columns,
  onError,
  parentRowIndex,
  nestedColLabel,
  rowIndexBase = 0,
}: {
  tableDs: any;
  columns: any;
  onError: (error: ValidateCellError) => void;
  parentRowIndex?: number;
  nestedColLabel?: string;
  /** 顶层行在整表中的起始下标。嵌套子表不传，仍从 0 计。 */
  rowIndexBase?: number;
}): boolean {
  const records = unref(tableDs) || [];
  const cols = (unref(columns) || []).filter((col: any) => col?.dataIndex || col?.propId);
  for (let index = 0; index < records.length; index++) {
    const rowIndex = rowIndexBase + index;
    const record = records[index];
    for (const column of cols) {
      if (isNestedDisplaySubTableColumn(column)) {
        const nestedRecords = getRecordSubTableRecords(record, column);
        const nestedColumns = column.dataConfig?.columns || [];
        if (nestedRecords.length && nestedColumns.length) {
          const colLabel = column.propName || column.title || getSubTableFieldKey(column);
          const passed = validateAllTableRecords({
            tableDs: nestedRecords,
            columns: nestedColumns,
            parentRowIndex: parentRowIndex ?? rowIndex,
            nestedColLabel: colLabel,
            onError,
          });
          if (!passed) {
            return false;
          }
        }
        continue;
      }
      const error = validateRecordCell({record, column, rowIndex});
      if (error) {
        onError({
          ...error,
          rowIndex: parentRowIndex ?? error.rowIndex,
          parentRowIndex,
          nestedRowIndex: parentRowIndex != null ? rowIndex : undefined,
          nestedColLabel: parentRowIndex != null ? nestedColLabel : undefined,
        });
        return false;
      }
    }
  }
  return true;
}
